import { useRef, useState, useEffect, useCallback } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { USE_HARDCODED_CONVERSATION, loadConversation } from "../api.js";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
const useConversationLoader = ({
  conversationId,
  sharedConversationId,
  onConversationLoaded,
  onConversationNotFound,
  isActivelyStreaming = false,
  conversationHistoryLength = 0,
  apiBaseUrl,
  viewToken
}) => {
  const queryClient = useQueryClient();
  const loadedConversationIdRef = useRef(null);
  const loadedSharedConversationIdRef = useRef(null);
  const loadingConversationIdRef = useRef(null);
  const [forceReload, setForceReload] = useState(0);
  const previousConversationIdRef = useRef(null);
  const wasStreamingRef = useRef(false);
  const [isPendingAssistantResponse, setIsPendingAssistantResponse] = useState(false);
  const [isPollingTimedOut, setIsPollingTimedOut] = useState(false);
  const pollingStartTimeRef = useRef(null);
  const POLLING_INTERVAL_MS = 3e3;
  const POLLING_TIMEOUT_MS = 18e4;
  const markLoaded = (id) => {
    if (conversationId) {
      loadedConversationIdRef.current = id;
    } else if (sharedConversationId) {
      loadedSharedConversationIdRef.current = id;
    }
  };
  useEffect(() => {
    setIsPendingAssistantResponse(false);
    setIsPollingTimedOut(false);
    pollingStartTimeRef.current = null;
  }, [conversationId, sharedConversationId]);
  useEffect(() => {
    const prevId = previousConversationIdRef.current;
    previousConversationIdRef.current = conversationId;
    if (isActivelyStreaming) {
      if (conversationId || USE_HARDCODED_CONVERSATION) {
        loadedConversationIdRef.current = conversationId || "hardcoded";
      }
      return;
    }
    if (wasStreamingRef.current && !isActivelyStreaming && conversationId === prevId) {
      if (conversationId || USE_HARDCODED_CONVERSATION) {
        loadedConversationIdRef.current = conversationId || "hardcoded";
      }
      return;
    }
    const effectiveId = conversationId || sharedConversationId || null;
    const isConversationNew = !!conversationId && conversationId !== loadedConversationIdRef.current;
    const isSharedNew = !!sharedConversationId && sharedConversationId !== loadedSharedConversationIdRef.current;
    if (effectiveId && (isConversationNew || isSharedNew)) {
      if (conversationHistoryLength > 0 && prevId === null) {
        if (conversationId) loadedConversationIdRef.current = conversationId;
        else if (sharedConversationId) loadedSharedConversationIdRef.current = sharedConversationId;
        return;
      }
      if (loadingConversationIdRef.current === effectiveId) {
        return;
      }
      loadingConversationIdRef.current = effectiveId;
      setForceReload((prev) => prev + 1);
      queryClient.invalidateQueries({
        queryKey: ["conversation", conversationId, sharedConversationId]
      });
    } else if (effectiveId && prevId === null && (!!conversationId && conversationId === loadedConversationIdRef.current || !!sharedConversationId && sharedConversationId === loadedSharedConversationIdRef.current)) {
      if (conversationId) loadedConversationIdRef.current = null;
      else if (sharedConversationId) loadedSharedConversationIdRef.current = null;
      setForceReload((prev) => prev + 1);
      queryClient.invalidateQueries({
        queryKey: ["conversation", conversationId, sharedConversationId]
      });
    }
  }, [conversationId, sharedConversationId, queryClient, isActivelyStreaming, conversationHistoryLength, forceReload]);
  useEffect(() => {
    wasStreamingRef.current = isActivelyStreaming;
  }, [isActivelyStreaming]);
  const {
    data: loadedConversationData,
    isLoading: isLoadingConversationQuery,
    isSuccess: isConversationQuerySuccess,
    error: conversationLoadError
  } = useQuery({
    queryKey: ["conversation", conversationId, sharedConversationId, forceReload, USE_HARDCODED_CONVERSATION],
    queryFn: async () => {
      if (!conversationId) {
        if (!sharedConversationId) return null;
      }
      const headers = await getHeadersWithUniqueVisitorId();
      return loadConversation({
        conversationId,
        sharedConversationId,
        headersToInject: headers,
        apiBaseUrl,
        viewToken
      });
    },
    enabled: (!!conversationId && conversationId !== loadedConversationIdRef.current || !!sharedConversationId && sharedConversationId !== loadedSharedConversationIdRef.current) && !isActivelyStreaming || isPendingAssistantResponse && !isPollingTimedOut && !!conversationId && !isActivelyStreaming,
    retry: false,
    staleTime: Infinity,
    refetchInterval: isPendingAssistantResponse && !isPollingTimedOut ? POLLING_INTERVAL_MS : false
  });
  useEffect(() => {
    const shouldProcess = loadedConversationData && (conversationId || sharedConversationId);
    if (shouldProcess) {
      const effectiveConversationId = conversationId || sharedConversationId || (loadedConversationData == null ? void 0 : loadedConversationData.conversationId) || "hardcoded";
      if (loadedConversationData.error === "unauthorized") {
        console.warn(`Unauthorized to access conversation ${effectiveConversationId}, redirecting`);
        onConversationNotFound();
        markLoaded(effectiveConversationId);
        loadingConversationIdRef.current = null;
        if (typeof window !== "undefined") {
          window.history.pushState({}, "", "/concierge");
        }
        return;
      }
      const processedMessages = (loadedConversationData.messages || []).filter((msg) => msg.role === "user" || msg.role === "assistant").filter((msg) => {
        if (msg.role === "user") return true;
        return Array.isArray(msg.parts) && msg.parts.length > 0;
      });
      const followUpQuestions = loadedConversationData.followUpQuestions || [];
      const conversationStatus = loadedConversationData.status || "completed";
      if (processedMessages.length > 0) {
        const sharingInfo = {
          isSharedView: loadedConversationData.isSharedView,
          sharedConversationId: loadedConversationData.sharedConversationId,
          sharedAtMessageIndex: loadedConversationData.sharedAtMessageIndex,
          sharedByUserName: loadedConversationData.sharedByUserName,
          sourceConversationId: loadedConversationData.sourceConversationId,
          sourceCheckpointIndex: loadedConversationData.sourceCheckpointIndex
        };
        onConversationLoaded(
          processedMessages,
          followUpQuestions,
          sharingInfo
        );
        markLoaded(effectiveConversationId);
        loadingConversationIdRef.current = null;
        const lastProcessedMessage = processedMessages[processedMessages.length - 1];
        if ((lastProcessedMessage == null ? void 0 : lastProcessedMessage.role) === "user") {
          if (!pollingStartTimeRef.current) {
            pollingStartTimeRef.current = Date.now();
          }
          setIsPendingAssistantResponse(true);
        } else {
          setIsPendingAssistantResponse(false);
          setIsPollingTimedOut(false);
          pollingStartTimeRef.current = null;
        }
      } else if (conversationStatus === "in_progress") {
        onConversationLoaded(
          [],
          followUpQuestions,
          {
            isSharedView: loadedConversationData.isSharedView,
            sharedConversationId: loadedConversationData.sharedConversationId,
            sharedAtMessageIndex: loadedConversationData.sharedAtMessageIndex,
            sharedByUserName: loadedConversationData.sharedByUserName,
            sourceConversationId: loadedConversationData.sourceConversationId,
            sourceCheckpointIndex: loadedConversationData.sourceCheckpointIndex
          }
        );
        if (!pollingStartTimeRef.current) {
          pollingStartTimeRef.current = Date.now();
        }
        setIsPendingAssistantResponse(true);
        markLoaded(effectiveConversationId);
        loadingConversationIdRef.current = null;
      } else {
        console.warn(`Conversation ${effectiveConversationId} not found, clearing URL`);
        onConversationNotFound();
        markLoaded(effectiveConversationId);
        loadingConversationIdRef.current = null;
      }
    } else if (isConversationQuerySuccess && loadedConversationData === null && (conversationId || sharedConversationId) && (conversationId ? loadedConversationIdRef.current !== conversationId : loadedSharedConversationIdRef.current !== sharedConversationId)) {
      const effectiveId = conversationId || sharedConversationId;
      console.warn(`Conversation ${effectiveId} not found (null response), clearing URL`);
      onConversationNotFound();
      if (conversationId) loadedConversationIdRef.current = conversationId;
      else if (sharedConversationId) loadedSharedConversationIdRef.current = sharedConversationId;
      loadingConversationIdRef.current = null;
    }
  }, [loadedConversationData, conversationId, sharedConversationId, isConversationQuerySuccess]);
  useEffect(() => {
    if (conversationLoadError && (conversationId || sharedConversationId)) {
      console.error("Failed to load conversation:", conversationLoadError);
      onConversationNotFound();
      if (conversationId) loadedConversationIdRef.current = conversationId;
      else if (sharedConversationId) loadedSharedConversationIdRef.current = sharedConversationId;
      loadingConversationIdRef.current = null;
    }
  }, [conversationLoadError, conversationId, sharedConversationId]);
  useEffect(() => {
    if (!isPendingAssistantResponse || isPollingTimedOut) return;
    const timer = setTimeout(() => {
      setIsPollingTimedOut(true);
      setIsPendingAssistantResponse(false);
    }, POLLING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isPendingAssistantResponse, isPollingTimedOut]);
  const resetPendingState = useCallback(() => {
    setIsPendingAssistantResponse(false);
    setIsPollingTimedOut(false);
    pollingStartTimeRef.current = null;
  }, []);
  const markConversationStale = useCallback(() => {
    loadedConversationIdRef.current = null;
    loadedSharedConversationIdRef.current = null;
    setForceReload((prev) => prev + 1);
  }, []);
  return {
    isLoadingConversationQuery,
    conversationLoadError,
    isPendingAssistantResponse,
    isPollingTimedOut,
    resetPendingState,
    markConversationStale
  };
};
export {
  useConversationLoader
};
//# sourceMappingURL=useConversationLoader.js.map
